import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  TextInput,
  Alert,
  Spinner,
  Pagination,
  Datepicker,
  Textarea,
} from 'flowbite-react';
import { fetchWithAuth } from '../utils/api';
import { getHolidaysEndpoints } from '../config/api';
import { getValidationMessages, formatDate } from '../utils/helper';

interface Holiday {
  id: number;
  day: string;
  created_at: string;
}

interface HolidayFormData {
  day: string;
}

const Holidays: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);
  const [messagePopup, setMessagePopup] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);

  // Form
  const [formData, setFormData] = useState<HolidayFormData>({
    day: formatDate(new Date()),
  });
  const [dayList, setDayList] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load list
  const loadHolidays = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const holidaysEndpoint = getHolidaysEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
        ...(search && { search: search }),
      });

      const response = await fetchWithAuth(`${holidaysEndpoint}?${params}`);

      if (response.success && response.data) {
        setHolidays(response.data.results || []);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
      } else {
        setError('Không thể tải danh sách ngày nghỉ');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load holidays error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHolidays(1, searchTerm);
  }, []);

  // Search
  const handleSearch = () => {
    setCurrentPage(1);
    loadHolidays(1, searchTerm);
  };

  // Pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadHolidays(page, searchTerm);
  };

  // Open create
  const openCreateModal = () => {
    setModalMode('create');
    setEditingHoliday(null);
    setFormData({
      day: formatDate(new Date()),
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
    setDayList([]);
  };

  // Open edit
  const openEditModal = (holiday: Holiday) => {
    setModalMode('edit');
    setEditingHoliday(holiday);
    setFormData({
      day: holiday.day,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Input change
  const handleInputChange = (field: keyof HolidayFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Change day list
  const handleChangeDayList = (value: string) => {
    setDayList(
      value
        .split(',')
        .map(day => day.trim())
        .filter(day => day !== '')
    );
  };

  // Validate
  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.day.trim()) {
      errors.day = 'Ngày nghỉ là bắt buộc';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const holidaysEndpoint = getHolidaysEndpoints();
      const endpoint =
        modalMode === 'create'
          ? holidaysEndpoint.CREATE
          : `${holidaysEndpoint.UPDATE}/${editingHoliday?.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const data =
        modalMode === 'create'
          ? dayList.map(day => ({ day: day }))
          : {
              day: formData.day,
            };

      const response = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify(data),
      });

      if (response.success) {
        setMessagePopup({
          type: 'success',
          text: `Ngày nghỉ ${
            modalMode === 'create' ? 'đã được tạo' : 'đã được cập nhật'
          } thành công!`,
        });
        setShowModal(false);
        loadHolidays(currentPage, searchTerm);
      } else {
        setMessagePopup({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            `Không thể ${
              modalMode === 'create' ? 'tạo' : 'cập nhật'
            } ngày nghỉ`,
        });
      }
    } catch (error) {
      setMessagePopup({
        type: 'failure',
        text: `Đã xảy ra lỗi khi ${
          modalMode === 'create' ? 'tạo' : 'cập nhật'
        } ngày nghỉ`,
      });
      console.error('Submit holiday error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete
  const handleDelete = async (holidayId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa ngày nghỉ này?')) {
      return;
    }

    try {
      const holidaysEndpoint = getHolidaysEndpoints();
      const response = await fetchWithAuth(
        `${holidaysEndpoint.DELETE}/${holidayId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Ngày nghỉ đã được xóa thành công!',
        });
        loadHolidays(currentPage, searchTerm);
      } else {
        setMessage({
          type: 'failure',
          text: 'Không thể xóa ngày nghỉ',
        });
      }
    } catch (error) {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi xóa ngày nghỉ',
      });
      console.error('Delete holiday error:', error);
    }
  };

  if (loading && holidays.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải danh sách ngày nghỉ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Ngày nghỉ</h1>
            <p className="text-gray-600">Quản lý các ngày nghỉ</p>
          </div>
          <Button onClick={openCreateModal} className="btn-primary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Tạo Ngày nghỉ
          </Button>
        </div>
      </div>

      {message && (
        <Alert color={message.type} className="mb-4">
          <span>{message.text}</span>
        </Alert>
      )}

      {/* Search */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="flex gap-4">
            <TextInput
              placeholder="Tìm kiếm..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleSearch} className="btn-primary">
              Tìm kiếm
            </Button>
          </div>
        </div>
      </Card>

      {/* Holidays Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>ID</Table.HeadCell>
              <Table.HeadCell>Ngày nghỉ</Table.HeadCell>
              <Table.HeadCell>Ngày tạo</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {holidays.map(holiday => (
                <Table.Row
                  key={holiday.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {holiday.id}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(holiday.day)}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(holiday.created_at)}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => openEditModal(holiday)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="sm"
                        color="red"
                        onClick={() => handleDelete(holiday.id.toString())}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4">
            <div className="text-sm text-gray-700">
              Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
              {totalItems} kết quả
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="2xl">
        <Modal.Header>
          {modalMode === 'create' ? 'Tạo Ngày nghỉ Mới' : 'Chỉnh Sửa Ngày nghỉ'}
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {messagePopup && (
              <Alert
                color={messagePopup.type}
                className="mb-4 whitespace-pre-line"
              >
                <span>{messagePopup.text}</span>
              </Alert>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ngày nghỉ *
                </label>
                {modalMode === 'edit' ? (
                  <Datepicker
                    value={formatDate(formData.day)}
                    defaultDate={new Date(formData.day)}
                    icon={undefined}
                    onSelectedDateChanged={e => {
                      handleInputChange('day', formatDate(e));
                    }}
                    color={formErrors.day ? 'failure' : undefined}
                    helperText={formErrors.day}
                    theme={{
                      views: {
                        days: {
                          items: {
                            base: 'grid w-64 grid-cols-7',
                            item: {
                              base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                              selected:
                                'bg-primary-700 text-blue-500 hover:bg-primary-600',
                              disabled: 'text-gray-500 line-through',
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Textarea
                    onChange={e => handleChangeDayList(e.target.value)}
                    placeholder="Nhập ngày nghỉ. Nhập nhiều ngày nghỉ cách nhau bằng dấu phẩy. Ví dụ: 2025-01-01, 2025-01-02, 2025-01-03"
                    color={formErrors.day ? 'failure' : undefined}
                    helperText={formErrors.day}
                  />
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="gray"
              onClick={() => setShowModal(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Đang xử lý...
                </>
              ) : modalMode === 'create' ? (
                'Tạo Ngày nghỉ'
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default Holidays;
